// @ts-strict-ignore
import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { useEmptyColumn } from "@dashboard/components/Datagrid/hooks/useEmptyColumn";
import Money from "@dashboard/components/Money";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination";
import { OrderListQuery } from "@dashboard/graphql";
import { OrderListUrlSortField } from "@dashboard/orders/urls";
import { ListProps, RelayToFlat, SortPage } from "@dashboard/types";
import { Item } from "@glideapps/glide-data-grid";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import * as XLSX from "xlsx";

import { orderListStaticColumnAdapter, useGetCellContent } from "./datagrid";
import { messages } from "./messages";
import { canBeSorted, getColumnNameAndId, getOrdersRowsLength } from "./utils";

interface OrderListDatagridProps extends ListProps, SortPage<OrderListUrlSortField> {
  orders: RelayToFlat<OrderListQuery["orders"]>;
  onRowClick?: (id: string) => void;
  rowAnchor?: (id: string) => string;
  hasRowHover?: boolean;
}

export const OrderListDatagrid: React.FC<OrderListDatagridProps> = ({
  orders,
  disabled,
  settings,
  onUpdateListSettings,
  onSort,
  sort,
  onRowClick,
  hasRowHover,
  rowAnchor,
}) => {
  const location = useLocation();
  const intl = useIntl();
  const datagrid = useDatagridChangeState();
  const ordersLength = getOrdersRowsLength(orders, disabled);
  const handleColumnChange = useCallback(
    picked => {
      onUpdateListSettings("columns", picked.filter(Boolean));
    },
    [onUpdateListSettings],
  );
  const emptyColumn = useEmptyColumn();
  const memoizedStaticColumns = useMemo(
    () => orderListStaticColumnAdapter(emptyColumn, intl, sort),
    [emptyColumn, intl, sort],
  );
  const { handlers, staticColumns, visibleColumns, selectedColumns } = useColumns({
    gridName: "order_list",
    staticColumns: memoizedStaticColumns,
    selectedColumns: settings?.columns ?? [],
    onSave: handleColumnChange,
  });
  const handleHeaderClick = useCallback(
    (col: number) => {
      const { columnName, columnId } = getColumnNameAndId(visibleColumns[col].id);

      if (canBeSorted(columnName)) {
        onSort(columnName, columnId);
      }
    },
    [visibleColumns, onSort],
  );
  const handleRowClick = useCallback(
    ([_, row]: Item) => {
      if (!onRowClick) {
        return;
      }

      const rowData = orders[row];

      onRowClick(rowData.id);
    },
    [onRowClick, orders],
  );
  const handleRowAnchor = useCallback(
    ([, row]: Item) => {
      if (!rowAnchor) {
        return;
      }

      const rowData = orders[row];

      return rowAnchor(rowData.id);
    },
    [rowAnchor, orders],
  );
  const getCellContent = useGetCellContent({
    columns: visibleColumns,
    orders,
  });

  const totalUsd = {
    amount: orders
      ? orders.reduce((acc, order) => {
          return acc + order.total.gross.amount;
        }, 0)
      : 0,
    currency: "USD",
  };

  const exportToExcel = orders => {
    let jsonOrders = orders.map(order => ({
      Number: order.number,
      Date: new Date(order.created).toLocaleString(),
      Customer: order.billingAddress
        ? `${order.billingAddress.firstName} ${order.billingAddress.lastName} (${order.userEmail})`
        : order.userEmail,
      Payment: order.paymentStatus,
      Status: order.status,
      Total: `${order.total.gross.currency} ${order.total.gross.amount}`,
      Warehouse: order.lines
        .map(
          line =>
            `${line.productName} - ${line.allocations.map(allocation => `${allocation.warehouse.name} - ${allocation.quantity}u`).join(", ")}`,
        )
        .join(" | "),
    }));

    jsonOrders = [
      ...jsonOrders,
      {
        Number: "Total",
        Date: "",
        Customer: "",
        Payment: "",
        Status: "",
        Total: `${totalUsd.currency} ${totalUsd.amount}`,
        Warehouse: "",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(jsonOrders);

    // Define styles
    const headerStyle = { font: { bold: true } };
    const columnWidths = [
      { wpx: 50 }, // Number
      { wpx: 150 }, // Date
      { wpx: 300 }, // Customer
      { wpx: 100 }, // Payment
      { wpx: 100 }, // Status
      { wpx: 100 }, // Total
      { wpx: 400 }, // Warehouse
    ];

    // Apply styles to header
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ c: C, r: 0 });

      if (!worksheet[cellAddress]) continue;

      worksheet[cellAddress].s = headerStyle;
    }

    // Set column widths
    worksheet["!cols"] = columnWidths;

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
    XLSX.writeFile(workbook, `orders_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`);
  };

  return (
    <Box __marginTop={ordersLength > 0 ? -1 : 0}>
      <DatagridChangeStateContext.Provider value={datagrid}>
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          paddingX={6}
          paddingY={2}
          gap={4}
        >
          <Text as="p">
            <strong>Total:</strong> <Money money={totalUsd} />{" "}
          </Text>
          <Button onClick={() => exportToExcel(orders)}>Export to Excel</Button>
        </Box>
        <Datagrid
          readonly
          rowMarkers="none"
          loading={disabled}
          columnSelect="single"
          hasRowHover={hasRowHover}
          freezeColumns={2}
          verticalBorder={false}
          availableColumns={visibleColumns}
          onHeaderClicked={handleHeaderClick}
          emptyText={intl.formatMessage(messages.emptyText)}
          getCellContent={getCellContent}
          getCellError={() => false}
          menuItems={() => []}
          rows={getOrdersRowsLength(orders, disabled)}
          selectionActions={() => null}
          onColumnResize={handlers.onResize}
          onColumnMoved={handlers.onMove}
          renderColumnPicker={() => (
            <ColumnPicker
              staticColumns={staticColumns}
              selectedColumns={selectedColumns}
              onToggle={handlers.onToggle}
            />
          )}
          onRowClick={handleRowClick}
          rowAnchor={handleRowAnchor}
          navigatorOpts={{
            state: {
              prevLocation: location,
            },
          }}
        />

        <Box paddingX={6}>
          <TablePaginationWithContext
            component="div"
            settings={settings}
            disabled={disabled}
            onUpdateListSettings={onUpdateListSettings}
          />
        </Box>
      </DatagridChangeStateContext.Provider>
    </Box>
  );
};
